// ZoneTip - hovering a zone on the stack shows it large (up to 1,180 px),
// exploded by height, with a table of what each system has there. Clicking
// the zone opens the full zone page in a new tab; on a phone the first tap
// shows this panel as a bottom sheet and the second opens the page.
import React, { useLayoutEffect, useRef, useState } from "react";
import ZoneBands from "./ZoneBands";
import {
  ROOF, STATUS_COLOR, STATUS_NAME, bucketCount, bucketKey, drawnSystems, isCovered, levelLabel, systemColor,
} from "./glanceModel";

export function zoneRows(manifest, level, zone, active) {
  return drawnSystems(manifest)
    .filter((s) => (!active || active.has(s.id)) && (level === ROOF ? s.id === ROOF : s.id !== ROOF))
    .map((s) => {
      const key = bucketKey(level, s.id, zone);
      const count = bucketCount((manifest.buckets || {})[key]);
      const covered = isCovered(s, level, zone);
      const unit = s.draw === "polys" ? "pathway pieces" : s.draw === "lines" ? "drawn segments" : "items";
      return { s, key, count, covered, text: !covered ? "Not in received drawings" : count ? `${count.toLocaleString()} ${unit}` : "None drawn" };
    });
}

export default function ZoneTip({ manifest, tip, active, statuses, mode, progressDate, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: 12, top: 12 });
  const phone = typeof window !== "undefined" && window.innerWidth <= 700;

  useLayoutEffect(() => {
    if (!tip || !ref.current || phone) return;
    const r = ref.current.getBoundingClientRect();
    const W = window.innerWidth; const H = window.innerHeight;
    let left; let top;
    if (tip.x == null) { left = Math.max(8, (W - r.width) / 2); top = 20; } else {
      left = tip.x > W / 2 ? 12 : W - r.width - 12;
      if (r.width > W * 0.7) left = (W - r.width) / 2;
      top = tip.y > H / 2 ? 8 : H - r.height - 8;
    }
    setPos({ left: Math.max(8, left), top: Math.max(8, Math.min(top, H - r.height - 8)) });
  }, [tip, phone]);

  if (!tip) return null;
  const { level, zone } = tip;
  const z = (manifest.zones || [])[zone];
  if (!z) return null;
  const rows = zoneRows(manifest, level, zone, active);

  return (
    <div ref={ref} className={`g-tip${phone ? " is-sheet" : ""}`} style={phone ? undefined : pos} role="dialog" aria-live="polite">
      <h4>{levelLabel(manifest, level)}, {z.label}</h4>
      <p className="g-tsub">
        {mode === "progress" ? `Progress on ${progressDate}` : level === ROOF ? "Roof equipment in this zone" : "Exploded by height: each band is lifted so you can see what stacks above what"}
      </p>
      <div className="g-tipsvg">
        <ZoneBands manifest={manifest} level={level} zone={zone} active={active} statuses={statuses} mode={mode} />
      </div>
      <table>
        <thead><tr><th>System</th><th>In this zone</th><th>{mode === "progress" ? "Status" : "Height and source"}</th></tr></thead>
        <tbody>
          {rows.map(({ s, key, text, count, covered }) => {
            const st = statuses && statuses[key];
            return (
              <tr key={s.id}>
                <td><i className="g-dot" style={{ background: systemColor(s) }} />{s.label}</td>
                <td>{text}</td>
                <td>
                  {mode === "progress" ? (
                    !count && covered ? "n/a" : st ? (
                      <>
                        <span className="g-pill" style={{ color: STATUS_COLOR[st.st] }}>{STATUS_NAME[st.st]} {st.pct}%</span>
                        {st.field && (
                          <div className="g-tsub">
                            From the field: {st.field.elements} element{st.field.elements === 1 ? "" : "s"}, by {st.field.basis}
                            {st.field.blocked ? `, ${st.field.blocked} of ${st.field.elements} blocked` : ""}
                            {st.field.inspected ? `, ${st.field.inspected} inspected` : ""}
                            {(st.field.issues || []).map((i) => <div key={i.id} style={{ color: "var(--block)" }}>{i.label ? `${i.label}: ` : ""}{i.title}</div>)}
                          </div>
                        )}
                        {st.activities.slice(0, 2).map((a) => (
                          <div key={a.id} className="g-tsub">
                            {a.name}
                            {a.checklists ? `, checklists ${a.checklists.passed} of ${a.checklists.total}` : ""}
                            {a.issues.map((i) => <div key={i.id} style={{ color: "var(--block)" }}>{i.label}: {i.title}</div>)}
                          </div>
                        ))}
                      </>
                    ) : <span className="g-tsub">Nothing tracked here yet</span>
                  ) : (
                    <>{s.band}<br /><span className="g-tsub">{s.source}</span></>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="g-open">
        {phone
          ? <a href={tip.href} target="_blank" rel="noopener noreferrer">Open the full zone view</a>
          : "Click this zone to open the full view with filters and item lists in a new tab"}
        {phone && <button type="button" className="g-linkbtn" onClick={onClose}>Close</button>}
      </p>
    </div>
  );
}
